import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FilterToggleButton } from '@/components/common/FilterToggleButton/FilterToggleButton'
import { useActiveFiltersCount } from '@/hooks/useActiveFilterCount/useActiveFilterCount'
import {
    DEFAULT_SUSTAINABILITY_FILTERING,
    SustainabilityFiltersValues,
} from '@/utils/constants'
import { ActiveFiltersCounter } from '@/components/common/ActiveFiltersCounter/ActiveFiltersCounter'
import {
    createSelectHasDefaultFilters,
    FiltersStoreState,
} from '@/stores/createFiltersStore'
import { TimelineFilterValue } from '@/components/common/Filter/types'
import { CommodityFilter } from '@/components/core/filters/CommodityFilter'
import { NodeFilter } from '@/components/core/filters/NodeFilter'
import { ContractFilter } from '@/components/core/filters/ContractFilter'
import { TagFilter } from '@/components/core/filters/TagFilter'
import { FacilityFilter } from '@/components/core/filters/FacilityFilter'
import { TimelineFilter } from '@/components/core/filters/TimelineFilter'
import { MeterFilter } from '@/components/core/filters/MeterFilter'
import { useLocation } from '@tanstack/react-router'
import { LinkButton } from '@/components/common/LinkButton/LinkButton'
import { useSustainabilityFiltersGetParams } from '../../hooks/useSustainabilityFiltersGetParams'
import { useSustainabilityFiltersStore } from '../../hooks/useSustainabilityFiltersStore'
import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'
import { TotalEmissionsReportType } from '@/hooks'
import classNames from 'classnames'

type SustainabilityFiltersContainerProps = {
    activeFilters: SustainabilityFiltersValues
    onChange: FiltersStoreState<SustainabilityFiltersValues>['updateActiveFilter']
    handleResetFilters: () => void
    showResetFiltersButton: boolean
    reportType?: string
}

export const SustainabilityFiltersContainer: React.FC<
    SustainabilityFiltersContainerProps
> = (props) => {
    const {
        activeFilters,
        onChange,
        handleResetFilters,
        showResetFiltersButton,
        reportType,
    } = props

    const { t } = useTranslation('translations')
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(true)

    const activeFiltersCount = useActiveFiltersCount(
        activeFilters,
        DEFAULT_SUSTAINABILITY_FILTERING
    )

    const handleToggleVisibility = () => {
        setIsVisible((prevVisible) => !prevVisible)
    }

    const { pathname } = useLocation()

    const isMeterNotAvailable = useMemo(() => {
        return pathname.includes('/ghg') || reportType === 'ghg-emissions'
    }, [pathname, reportType])

    const hoveredDisplaySetting = useSustainabilityStore(
        (state) => state.totalEmissions.hoveredDisplaySetting
    )

    const reportSettings = useSustainabilityStore(
        (state) => state.totalEmissions.reportSettings
    )

    const params = useSustainabilityFiltersGetParams(
        activeFilters,
        reportSettings
    )

    const selectedFilter = useMemo(() => {
        if (reportSettings.type === 'commodities') {
            return 'commodity'
        }

        return reportSettings.type
    }, [reportSettings.type])

    const getSustainabilityFilterHighlightClass = useCallback(
        (displaySetting: TotalEmissionsReportType) => {
            const filterType =
                displaySetting === 'commodities' ? 'commodity' : displaySetting
            const isSelected = selectedFilter === filterType
            const isHovered = hoveredDisplaySetting === displaySetting

            return classNames({
                'outline outline-4 outline-blue-200': isHovered,
                '!shadow-inset-azure !border-azure': isSelected,
            })
        },
        [hoveredDisplaySetting, selectedFilter]
    )

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-4" ref={containerRef}>
                <div
                    className="flex items-center justify-between gap-2"
                    data-testid="sustainability-filters-title-row"
                >
                    <div className="text-heading-6 py-2.5">
                        {t('filters.title')}
                    </div>
                    {!isVisible && (
                        <ActiveFiltersCounter count={activeFiltersCount} />
                    )}
                </div>
                {isVisible && (
                    <>
                        <NodeFilter
                            name="sustainability-node-filter"
                            value={activeFilters.node.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.node.map(
                                (id) => id.toString()
                            )}
                            containerRef={containerRef}
                            onChange={(value: string[]) => {
                                onChange(
                                    'node',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                        />
                        <CommodityFilter
                            name="sustainability-commodity-filter"
                            containerRef={containerRef}
                            multiple={true}
                            value={activeFilters.commodity.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.commodity.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'commodity',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                reportType: 'sustainability',
                            }}
                            className={getSustainabilityFilterHighlightClass(
                                'commodities'
                            )}
                        />

                        <ContractFilter
                            name="sustainability-contract-filter"
                            containerRef={containerRef}
                            value={activeFilters.contract.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.contract.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'contract',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                            }}
                        />
                        <TagFilter
                            name="sustainability-tag-filter"
                            containerRef={containerRef}
                            value={activeFilters.tag.map((id) => id.toString())}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.tag.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'tag',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'contractIds[]': params['contractIds[]'],
                            }}
                        />
                        <FacilityFilter
                            name="sustainability-facility-filter"
                            containerRef={containerRef}
                            value={activeFilters.facility.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.facility.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'facility',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'tagIds[]': params['tagIds[]'],
                            }}
                        />
                        <MeterFilter
                            name="sustainability-meter-filter"
                            containerRef={containerRef}
                            value={activeFilters.meter.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_SUSTAINABILITY_FILTERING.meter.map(
                                (id) => id.toString()
                            )}
                            notAvailable={isMeterNotAvailable}
                            onChange={(value: string[]) => {
                                onChange(
                                    'meter',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]':
                                    params['commodityTypeIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                            }}
                        />
                        <TimelineFilter
                            name="sustainability-timeline-filter"
                            value={activeFilters.timeline}
                            defaultValue={
                                DEFAULT_SUSTAINABILITY_FILTERING.timeline
                            }
                            onChange={(value: TimelineFilterValue) => {
                                onChange('timeline', value)
                            }}
                            containerRef={containerRef}
                        />
                    </>
                )}
                {showResetFiltersButton && (
                    <LinkButton
                        name="reset-filters"
                        iconBefore="sync"
                        label={t('filters.reset')}
                        onClick={handleResetFilters}
                    />
                )}
                <FilterToggleButton
                    isVisible={isVisible}
                    onToggle={handleToggleVisibility}
                />
            </div>
        </div>
    )
}

export const SustainabilityFilters: React.FC = () => {
    const updateActiveFilter = useSustainabilityFiltersStore(
        (state) => state.updateActiveFilter
    )

    const activeFilters = useSustainabilityFiltersStore(
        (state) => state.activeFilters
    )

    const selectHasDefaultFilters = createSelectHasDefaultFilters(
        DEFAULT_SUSTAINABILITY_FILTERING
    )

    const storeShowResetFiltersButton = !useSustainabilityFiltersStore(
        selectHasDefaultFilters
    )

    const handleResetActiveFilters = useSustainabilityFiltersStore(
        (state) => state.resetActiveFilters
    )

    return (
        <div className="relative z-30 border-b border-neutral-200 px-6 pb-6">
            <SustainabilityFiltersContainer
                onChange={updateActiveFilter}
                activeFilters={activeFilters}
                handleResetFilters={handleResetActiveFilters}
                showResetFiltersButton={storeShowResetFiltersButton}
            />
        </div>
    )
}
